import { useEffect, useState } from 'react';
import { AddLocationAlt } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Typography from '@/components/ui/Typography/Typography';
import Loading from '@/components/business/Loading/Loading';
import Page from '@/components/business/Page/Page';
import { useLocations } from '@/providers/LocationsProvider/LocationsProvider';
import type { Location } from '@/types/types';
import LocationCard from './components/LocationCard/LocationCard';
import LocationDetails from './components/LocationDetails/LocationDetails';
import NewLocationModal from './components/NewLocationModal/NewLocationModal';
import type { LocationDetailsFormValues } from './components/LocationDetails/ILocationDetails';
import { styles } from './styles';

const toLocationPayload = (form: LocationDetailsFormValues): Partial<Location> => ({
    nickname: form.nickname || null,
    formatted_address: form.formatted_address || null,
    street_number: form.street_number || null,
    street_name: form.street_name || null,
    subpremise: form.subpremise || null,
    city: form.city || null,
    state: form.state || null,
    zip: form.zip || null,
    country: form.country || null,
    notes: form.notes || null,
    place_id: form.place_id,
    contact_info: {
        name: form.contact_name || null,
        email: form.contact_email || null,
        phone_number: form.contact_phone || null,
    },
});

const LocationsPage = () => {
    const theme = useTheme();
    const locationsCtx = useLocations();
    const [newOpen, setNewOpen] = useState(false);

    useEffect(() => {
        void locationsCtx.actions.refreshLocations();
    }, []);

    const { locations, activeLocation, loading, loaded } = locationsCtx.state;

    const handleSave = async (values: LocationDetailsFormValues) => {
        if (!activeLocation) return;
        await locationsCtx.actions.updateLocation(
            activeLocation.id,
            toLocationPayload(values),
        );
    };

    const handleCreate = async (values: LocationDetailsFormValues) => {
        await locationsCtx.actions.createLocation(toLocationPayload(values));
    };

    return (
        <Page>
            <Box sx={styles.titleAndActions}>
                <Typography variant="h1">Locations</Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddLocationAlt />}
                    sx={styles.addLocationButton}
                    onClick={() => setNewOpen(true)}
                >
                    Add Location
                </Button>
            </Box>
            <Box sx={styles.contentContainer(theme)}>
                <Box sx={styles.layoutRow}>
                    <Box sx={styles.listPanel(theme, Boolean(activeLocation))}>
                        {loading && !loaded ? (
                            <Loading
                                loading
                                fullScreen={false}
                                width={120}
                                height={120}
                                timeoutMessage="Could not load locations."
                            />
                        ) : locations.length === 0 ? (
                            <Box sx={styles.emptyState}>
                                <Typography>No locations yet.</Typography>
                            </Box>
                        ) : (
                            locations.map((loc) => (
                                <LocationCard
                                    key={loc.id}
                                    location={loc}
                                    isActive={loc.id === activeLocation?.id}
                                    onClick={locationsCtx.actions.setActiveLocationById}
                                />
                            ))
                        )}
                    </Box>
                    <Box sx={styles.detailsPanel(!activeLocation)}>
                        {activeLocation ? (
                            <>
                                <Box
                                    sx={styles.mobileBackBar(theme)}
                                    onClick={() =>
                                        locationsCtx.actions.setActiveLocation(null)
                                    }
                                >
                                    ← Back to locations
                                </Box>
                                <LocationDetails
                                    key={activeLocation.id}
                                    location={activeLocation}
                                    onSave={handleSave}
                                />
                            </>
                        ) : (
                            <Box sx={styles.emptyState}>
                                <Typography>Select a location to see details.</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <NewLocationModal
                open={newOpen}
                onClose={() => setNewOpen(false)}
                onCreate={handleCreate}
            />
        </Page>
    );
};

export default LocationsPage;
